/**
 * Role-based access control helpers.
 * Reads auth user from localStorage (same source used on login).
 */

export const ROLES = {
  ADMIN: "admin",
  MANAGER: "manager",
  /** Matches API role "readOnly" (compared case-insensitively) */
  READ_ONLY: "readonly",
};

/** HTTP methods that change server state */
export const MUTATING_HTTP_METHODS = ["post", "put", "patch", "delete"];

/**
 * APIs that readOnly users may still call even if the method is mutating.
 * Includes auth lifecycle + POST-as-GET endpoints + soft notification UX.
 */
export const READ_ONLY_ALLOWED_API_PATTERNS = [
  /^\/auth\/admin\/login(\/v2)?(\?|$)/,
  /^\/auth\/admin\/mfa\/(setup|enable|verify)(\?|$)/,
  /^\/auth\/refresh(\?|$)/,
  /^\/auth\/logout(\?|$)/,
  /^\/auth\/signin(\?|$)/,
  /^\/auth\/signup(\?|$)/,
  /^\/crm\/ibcommission\/get(\?|$)/,
  /^\/notifications\/readAll(\?|$)/,
];

/** Top-level menu section IDs hidden from manager role */
export const MANAGER_HIDDEN_MENU_IDS = [
  "partners",
  "trades",
  "setting",
  "admins",
];

/** Account submenu IDs hidden from manager role */
export const MANAGER_HIDDEN_ACCOUNT_SUBMENU_IDS = ["liveTradingAccounts" , "myntLiveTradingAccounts"];

/** Routes hidden from and blocked for manager role */
export const MANAGER_RESTRICTED_ROUTES = [
  // Partners
  "/partners-list",
  "/partners-currency",
  "/partners-assign-acc-type",
  "/partners-commission-settings",
  // Live Trades page (Account > Live Trading Accounts)
  "/account-live-trading",
  "/mynt-live-trading-accounts",
  "/account-live-exposure",
  // Trades
  "/trades-live",
  "/trades-closed",
  "/trades-short-duration",
  // Settings
  "/setting-leverage",
  "/setting-acc-groups-limits",
  "/setting-payment-methods",
  "/setting-deposit-withdraw-conversion",
  "/setting-country-config",
  "/payments-deposit-payment",
  "/payments-withdraw-payment",
  // Admins
  "/admins-list",
  "/admins-login-records",
];

/** Dynamic route prefixes blocked for manager role */
export const MANAGER_RESTRICTED_ROUTE_PREFIXES = ["/partner-details/"];

/** API paths blocked for manager role (Partners section) */
export const MANAGER_RESTRICTED_PARTNERS_API_PATTERNS = [
  /^\/crm\/currency/,
  /^\/crm\/ibcommission/,
  /^\/crm\/mt5\/accounttypes(\?|$)/,
  /^\/crm\/mt5\/symbolscategory(\?|$)/,
  /^\/admin\/ib\/requests(\?|$)/,
  /^\/crm\/IBrequest/,
  /^\/crm\/partner/,
  /^\/crm\/commission\/history(\?|$)/,
  /^\/crm\/ibclient/,
  /^\/crm\/mt5\/user\/group/,
];

/** API paths blocked for manager role (Trades section; shared account APIs excluded) */
export const MANAGER_RESTRICTED_TRADES_API_PATTERNS = [
  /^\/admin\/demo\/orders(\?|$)/,
  /^\/admin\/orders\/v2\/bbook(\?|$)/,
  /^\/admin\/orders\/v2\/abook(\?|$)/,
  /^\/admin\/updateOrder(\?|$)/,
  /^\/admin\/running\/positions\/users(\?|$)/,
  /^\/crm\/mt5\/running\/positions(\?|$)/,
];

/** API paths blocked for manager role (LiveTrades page) */
export const MANAGER_RESTRICTED_LIVE_TRADES_API_PATTERNS = [
  /^\/crm\/mt5\/account\/live(\?|$)/,
  /^\/crm\/accounts\/live\/balance\/sum/,
  /^\/crm\/accounts\/live\/list/,
  /^\/crm\/mynt\/orders\/margin\/sum/,
];

/** API paths blocked for manager role (Settings section) */
export const MANAGER_RESTRICTED_SETTINGS_API_PATTERNS = [
  /^\/admin\/settings/,
  /^\/admin\/payment\/method/,
  /^\/admin\/payment\/methods/,
  /^\/crm\/country\/config/,
];

/** Combined API patterns blocked for manager role */
export const MANAGER_RESTRICTED_API_PATTERNS = [
  ...MANAGER_RESTRICTED_PARTNERS_API_PATTERNS,
  ...MANAGER_RESTRICTED_TRADES_API_PATTERNS,
  ...MANAGER_RESTRICTED_LIVE_TRADES_API_PATTERNS,
  ...MANAGER_RESTRICTED_SETTINGS_API_PATTERNS,
];

/**
 * Parse authUser from localStorage without depending on api_helper (avoids circular imports).
 * @returns {object|null}
 */
export const getStoredAuthUser = () => {
  try {
    const raw = localStorage.getItem("authUser");
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

/**
 * Extract the user profile object from stored auth payload.
 * Supports API, JWT, fake, and legacy shapes.
 * @returns {object|null}
 */
export const getAuthUserData = () => {
  const authUser = getStoredAuthUser();
  if (!authUser) return null;

  if (authUser.user && typeof authUser.user === "object") {
    return authUser.user;
  }
  if (authUser.data && typeof authUser.data === "object") {
    return authUser.data;
  }
  if (authUser.role) {
    return authUser;
  }

  return null;
};

/**
 * @returns {string} Normalized role string or empty string if unavailable
 */
export const getUserRole = () => {
  const userData = getAuthUserData();
  const role = userData?.role;
  return role ? String(role).trim() : "";
};

/**
 * @returns {boolean}
 */
export const isAdmin = () => {
  return getUserRole().toLowerCase() === ROLES.ADMIN;
};

/**
 * @returns {boolean}
 */
export const isManager = () => {
  return getUserRole().toLowerCase() === ROLES.MANAGER;
};

/**
 * True when the logged-in user has the readOnly role.
 * @returns {boolean}
 */
export const isReadOnly = () => {
  return getUserRole().toLowerCase() === ROLES.READ_ONLY;
};

/**
 * True when the current user is allowed to create/update/delete data.
 * Managers keep their existing section restrictions; readOnly cannot mutate.
 * @returns {boolean}
 */
export const canEdit = () => {
  return !isReadOnly();
};

/**
 * @param {string} [method]
 * @returns {boolean}
 */
export const isMutatingHttpMethod = (method = "") => {
  return MUTATING_HTTP_METHODS.includes(String(method).toLowerCase());
};

/**
 * @param {string} pathname
 * @returns {boolean}
 */
export const isManagerRestrictedRoute = (pathname = "") => {
  if (!pathname) return false;
  const normalizedPath = pathname.split("?")[0].replace(/\/+$/, "") || "/";

  if (
    MANAGER_RESTRICTED_ROUTE_PREFIXES.some(
      (prefix) =>
        normalizedPath === prefix.replace(/\/$/, "") ||
        normalizedPath.startsWith(prefix)
    )
  ) {
    return true;
  }

  return MANAGER_RESTRICTED_ROUTES.some(
    (route) =>
      normalizedPath === route || normalizedPath.startsWith(`${route}/`)
  );
};

const normalizeApiUrl = (url = "") => {
  if (!url) return "";
  const withoutQuery = url.split("?")[0];
  return withoutQuery.startsWith("/") ? withoutQuery : `/${withoutQuery}`;
};

const matchesApiPatterns = (url, patterns) => {
  const normalizedUrl = normalizeApiUrl(url);
  return patterns.some((pattern) => pattern.test(normalizedUrl));
};

/**
 * @param {string} url
 * @returns {boolean}
 */
export const isManagerRestrictedApiUrl = (url) => {
  return matchesApiPatterns(url, MANAGER_RESTRICTED_API_PATTERNS);
};

/**
 * @param {string} url
 * @returns {boolean}
 */
export const isReadOnlyAllowedApiUrl = (url) => {
  return matchesApiPatterns(url, READ_ONLY_ALLOWED_API_PATTERNS);
};

/**
 * Whether a request should be blocked for a readOnly user.
 * @param {string} [method]
 * @param {string} [url]
 * @returns {boolean}
 */
export const isReadOnlyMutationBlocked = (method, url) => {
  if (!isReadOnly()) return false;
  if (!isMutatingHttpMethod(method)) return false;
  if (isReadOnlyAllowedApiUrl(url)) return false;
  return true;
};

/**
 * Reject shape aligned with api_helper response interceptor errors.
 * @returns {{ message: string, status: number, data: { message: string } }}
 */
export const createAccessDeniedError = () => ({
  message: "Access Denied",
  status: 403,
  data: { message: "Access Denied" },
});

/**
 * Reject shape for read-only mutation attempts.
 * @returns {{ message: string, status: number, data: { message: string } }}
 */
export const createReadOnlyDeniedError = () => ({
  message: "Access Denied: Read-only users cannot modify data",
  status: 403,
  data: { message: "Access Denied: Read-only users cannot modify data" },
});

/**
 * Throws access denied error when current user is a manager and URL is restricted.
 * @param {string} url
 */
export const assertManagerAccess = (url) => {
  if (!isManager()) return;
  if (isManagerRestrictedApiUrl(url)) {
    throw createAccessDeniedError();
  }
};

/**
 * Throws when current user is readOnly and the request would mutate data.
 * @param {string} [method]
 * @param {string} [url]
 */
export const assertCanMutate = (method, url) => {
  if (isReadOnlyMutationBlocked(method, url)) {
    throw createReadOnlyDeniedError();
  }
};
